import { type JSONSchemaType } from 'ajv/dist/types/json-schema';
import { PropertyTypes } from './property-types';
import {
  type ObjectType,
  type PropertiesType,
} from '@wix/ambassador-forms-v4-form/types';
import { propertySchemaMapper } from './property-schema-mapper';
import { ValidationMode } from '@wix/ambassador-forms-v1-validation-result/types';

export function mapToObjectSchemaProperty(
  objectField: ObjectType,
  validationMode: ValidationMode,
): Omit<JSONSchemaType<object>, 'required'> & { required: string[] } {
  const properties = buildPropertiesSchema(
    validationMode,
    objectField.properties,
  );

  return {
    type: PropertyTypes.OBJECT,
    additionalProperties: false,
    properties,
    ...resolveRestrictedValidationProperties(objectField, validationMode),
  };
}

function resolveRestrictedValidationProperties(
  objectField: ObjectType,
  validationMode: ValidationMode,
) {
  if (validationMode !== ValidationMode.UNRESTRICTED_UPDATE) {
    return {
      required: fetchRequiredProperties(objectField.properties),
    };
  }
  return {
    required: [],
  };
}

function fetchRequiredProperties(
  properties: Record<string, PropertiesType> = {},
): string[] {
  const propertiesNames = Object.keys(properties);
  return propertiesNames.reduce<string[]>((acc, propertyName) => {
    const property = properties[propertyName];
    const isRequired = isRequiredProperty(property);
    if (isRequired) {
      acc.push(propertyName);
    }
    return acc;
  }, []);
}

function isRequiredProperty(property?: PropertiesType) {
  return (property || { required: false }).required;
}

function buildPropertiesSchema(
  validationMode: ValidationMode,
  properties?: Record<string, PropertiesType>,
) {
  if (properties) {
    const propertiesNames = Object.keys(properties);
    return propertiesNames.reduce<{ [key: string]: object }>(
      (acc, propertyName) => {
        const property = properties[propertyName];
        const propertyValidationSchema = propertySchemaMapper(
          property,
          undefined,
          validationMode,
        );
        if (propertyValidationSchema) {
          acc[propertyName] = propertyValidationSchema;
        }
        return acc;
      },
      {},
    );
  }
  return {};
}
